import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Button, Popconfirm, Space, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import { CalendarFilled, PlayCircleFilled, PoweroffOutlined, QuestionCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Actions
import { cycle_delete, cycle_end, cycle_start, get_all_cycles } from '../../redux/actions/cyclesActions';

// Utils
import { get_area_type_desc, get_cycle_scope_desc, get_cycle_status_desc } from '../../utils/get_status_desc';
import { get_cycle_status_color } from '../../utils/get_color_status';

const CyclesTable = (props) => {
	const { messageApi, orgFilters, setIsModalOpen, setCycleAction, setCycleToUpdate, pageSize, skip, setSkip, onRowClick, selectedCycle, filteredData } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const { all_cycles_loading, all_cycles, cycle_delete_loading } = useSelector(state => state.cycles);

	// Helper function to safely extract cycle ID
	const getCycleId = (record) => {
		return record._id?.$oid || record._id || record.id;
	};

	const updateCycle = (record) => {
		setIsModalOpen(true);
		setCycleAction('update');
		setCycleToUpdate(record);
	}

	const startCycle = (record) => {
		const filters = { ...orgFilters, skip: skip, limit: pageSize };
		dispatch(cycle_start(messageApi, getCycleId(record), filters));
	}

	const endCycle = (record) => {
		const filters = { ...orgFilters, skip: skip, limit: pageSize };
		dispatch(cycle_end(messageApi, getCycleId(record), filters));
	}

	const extendCycle = (record) => {
		setIsModalOpen(true);
		setCycleAction('extend');
		setCycleToUpdate(record);
	}

	const deleteCycle = (record) => {
		const filters = { ...orgFilters, skip: skip, limit: pageSize };
		dispatch(cycle_delete(messageApi, getCycleId(record), filters));
	}

	const renderDate = (value) => {
		const raw = value?.$date ?? value;
		const date = new Date(raw);
		const isValid = !isNaN(date.getTime());

		return (
			<Typography.Text>
				{isValid
					? date.toLocaleString(localStorage.getItem('i18nextLng') || 'es-MX', { hour12: true })
					: '-'}
			</Typography.Text>
		);
	};

	const columns = [
		{
			title: t('routesTable.headers.name'),
			dataIndex: 'name',
			key: 'name',
			render: (_, record) => (
				<Typography.Text copyable={{ text: getCycleId(record), tooltips: [t('copyableText.copyId'), t('copyableText.copiedId')] }}>
					{record.name}
				</Typography.Text>
			)
		},
		{
			title: t('routesTable.headers.status'),
			dataIndex: 'status',
			key: 'status',
			render: (_, record) => (
				<Tag color={get_cycle_status_color(record?.status)}>{get_cycle_status_desc(record?.status)}</Tag>
			)
		},
		// {
		// 	title: t('cycle.type'),
		// 	dataIndex: 'area_type',
		// 	key: 'area_type',
		// 	render: (text) => (
		// 		<Typography.Text>{get_area_type_desc(text)}</Typography.Text>
		// 	)
		// },
		// {
		// 	title: 'Scope',
		// 	dataIndex: 'scope',
		// 	key: 'scope',
		// 	render: (text) => (
		// 		<Typography.Text>{get_cycle_scope_desc(text)}</Typography.Text>
		// 	)
		// },
		{
			title: t('cycle.creationDate'),
			key: 'creation_date',
			render: (_, record) => renderDate(record.creation_date || record.date)
		},
		{
			title: t('cycle.selectStart'),
			dataIndex: 'start',
			key: 'start',
			render: renderDate
		},
		{
			title: t('cycle.selectEnd'),
			dataIndex: 'end',
			key: 'end',
			render: renderDate
		},
		{
			title: t('routesTable.headers.action'),
			dataIndex: 'action',
			key: 'action',
			render: (_, record) => (
				<Space>
					<Tooltip title={t('loadForm.edit.title').split(' ')[0]}>
						<Button
							type='primary'
							shape='circle'
							size='large'
							icon={<i className='bi bi-pencil-fill'></i>}
							onClick={() => updateCycle(record)}
						/>
					</Tooltip>
					{/* If the cycle status is 1 (CYCLE_STATUS_CREATED), the start cycle button will be displayed */}
					{record.status === 1 &&
						<Popconfirm
							title={t('cycle.startAction.popconfirm.title')}
							description={
								<Space direction='vertical' style={{ rowGap: 0 }}>
									<Typography.Text>{t('cycle.startAction.popconfirm.question')}</Typography.Text>
								</Space>
							}
							icon={<QuestionCircleFilled style={{ color: '#1677FF' }} />}
							onConfirm={() => startCycle(record)}
							okText={t('cycle.startAction.popconfirm.confirm')}
							cancelText={t('cycle.startAction.popconfirm.cancel')}
						>
							<Button
								type='primary'
								shape='circle'
								size='large'
								className='success-btn'
								icon={<PlayCircleFilled style={{ marginTop: '4px' }}/>}
							/>
						</Popconfirm>
					}
					{/* If the cycle status is 3 (CYCLE_STATUS_AVAILABLE), the end cycle button will be displayed */}
					{record.status === 3 &&
						<Popconfirm
							title={t('cycle.endAction.popconfirm.title')}
							description={
								<Space direction='vertical' style={{ rowGap: 0 }}>
									<Typography.Text>{t('cycle.endAction.popconfirm.question')}</Typography.Text>
								</Space>
							}
							icon={<QuestionCircleFilled style={{ color: '#1677FF' }} />}
							onConfirm={() => endCycle(record)}
							okText={t('cycle.endAction.popconfirm.confirm')}
							cancelText={t('cycle.endAction.popconfirm.cancel')}
						>
							<Button
								type='primary'
								shape='circle'
								size='large'
								style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
								icon={<PoweroffOutlined style={{ marginTop: '4px' }}/>}
							/>
						</Popconfirm>
					}
					{/* If the cycle status is 5 (CYCLE_STATUS_COMPLETED), the extend cycle button will be displayed */}
					{record.status === 5 &&
						<Tooltip title={t('cycle.extend.title').split(' ')[0]}>
							<Button
								type='primary'
								shape='circle'
								size='large'
								icon={<CalendarFilled style={{ marginTop: '4px' }}/>}
								onClick={() => extendCycle(record)}
							/>
						</Tooltip>
					}
					<Popconfirm
						title={t('cycle.delete.popconfirm.title')}
						description={t('cycle.delete.popconfirm.description')}
						icon={<QuestionCircleFilled style={{ color: '#1677FF' }} />}
						onConfirm={() => deleteCycle(record)}
						okButtonProps={{
							loading: cycle_delete_loading
						}}
						okText={t('cycle.delete.popconfirm.confirm')}
						cancelText={t('cycle.delete.popconfirm.cancel')}
					>
						<Button
							type='primary'
							shape='circle'
							size='large'
							danger
							icon={<span className='material-symbols-outlined'>delete</span>}
							style={{ 
								backgroundColor: '#ff4d4f',
								borderColor: '#ff4d4f',
								color: 'white'
							}}
						/>
					</Popconfirm>
				</Space>
			)
		}
	];

	const handleTableChange = (pagination, filters, sorter) => {
		const skip = (pagination.current - 1) * pageSize;
		setSkip(skip);

		const filesFilters = { ...orgFilters, skip: skip, limit: pageSize, sort: true };
		dispatch(get_all_cycles(filesFilters));
	};

	return (
		<div className='mt-5'>
			<Spin spinning={all_cycles_loading} tip={t('cycle.loading')} size='large'>
				<Table
					columns={columns}
					dataSource={filteredData || all_cycles.cycles}
					onChange={handleTableChange}
					pagination={filteredData ? false : {
						pageSize: pageSize,
						total: all_cycles.count,
						showTotal: (total, range) => `${range[0]} - ${range[1]} ${t('pagination.of')} ${total}`,
						current: (skip / pageSize) + 1
					}}
					scroll={{
						x: 1000
					}}
					onRow={(record) => ({
						onClick: () => {
							if (onRowClick) {
								onRowClick(record);
							}
						},
						style: {
							cursor: onRowClick ? 'pointer' : 'default'
						}
					})}
					rowClassName={(record) => selectedCycle && getCycleId(selectedCycle) === getCycleId(record) ? 'selected-cycle-row' : ''}
					rowKey={(record) => getCycleId(record)}
				/>
			</Spin>
		</div>
	)
}

CyclesTable.propTypes = {
	messageApi: PropTypes.object,
	orgFilters: PropTypes.object.isRequired,
	setIsModalOpen: PropTypes.func.isRequired,
	setCycleAction: PropTypes.func.isRequired,
	setCycleToUpdate: PropTypes.func.isRequired,
	pageSize: PropTypes.number.isRequired,
	skip: PropTypes.number.isRequired,
	setSkip: PropTypes.func.isRequired,
	onRowClick: PropTypes.func,
	selectedCycle: PropTypes.object,
	filteredData: PropTypes.array
}

export default CyclesTable;
